import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {bindActionCreators} from 'redux';
import * as actions from '../actions/productsActions';
import Loading from './Loading';
import SearchResultsFilter from './SearchResultsFilter';
import ProductsList from './ProductsList';
import {formatNumber} from '../utils/number';
import {attemptAddToCart} from '../utils/orderHandling';

const filterNames = [
  'categories',
  'colors',
  'conditions',
  'pageYields',
  'mlVolumes',
  'lVolumes',
  'staplesYields',
  'photosYields',
  'packAmounts',
  'sizes',
  'isPreviouslyPurchasedOnly',
];

class AppSearch extends Component {
  constructor(props) {
    super(props);

    // This binding is necessary to make `this` work in the callback
    this.fetchProducts = this.fetchProducts.bind(this);
    this.fetchFilters = this.fetchFilters.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handlePageChange = this.handlePageChange.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.resetSearch = this.resetSearch.bind(this);
    this.handleAddPrinter = this.handleAddPrinter.bind(this);
    this.handleRemovePrinter = this.handleRemovePrinter.bind(this);
    this.resetFilters = this.resetFilters.bind(this);
    this.handleSort = this.handleSort.bind(this);
    this.handleAddToMyProducts = this.handleAddToMyProducts.bind(this);
    this.handleRemoveFromMyProducts = this.handleRemoveFromMyProducts.bind(this);
    this.handlePerPageChange = this.handlePerPageChange.bind(this);
    this.handleReset = this.handleReset.bind(this);
    this.handleQtyChange = this.handleQtyChange.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.handleBackButton = this.handleBackButton.bind(this);
    this.processHash = this.processHash.bind(this);
    this.updateHash = this.updateHash.bind(this);
    this.hashToState = this.hashToState.bind(this);
    this.handleEmailLinkClick = this.handleEmailLinkClick.bind(this);

    window.state = this.state = this.getStateFromHash();
  }

  getStateFromHash() {
    const hash = document.location.hash.replace(/^#\//, ''),
      hashedState = this.hashToState(hash);

    return {
      sort: hashedState.sort || 'relevance',
      page: hashedState.page || 0,
      perPage: hashedState.perPage || 20,
      searchTerm: hashedState.searchTerm || this.props.searchTerms || '',
      quantities: {},

      filters: Object.assign(this.getDefaultFilters(), hashedState.filters),
      newFilters: Object.assign(this.getDefaultFilters(), hashedState.filters)
    };
  }

  processHash() {
    this.setState(this.getStateFromHash(), this.fetchProducts);
  }

  getDefaultFilters() {
    let filters = {};

    filterNames.map(name => {
      filters[name] = [];
    });

    return filters;
  }

  stateToHash() {
    const filters = this.state.filters;
    let hash = [];

    if (this.state.page) {
      hash.push(`page=${this.state.page}`);
    }
    if (this.state.sort) {
      hash.push(`sort=${this.state.sort}`);
    }
    if (this.state.perPage) {
      hash.push(`perPage=${this.state.perPage}`);
    }

    if (!this.props.searchTerms && this.state.searchTerm !== '') {
      hash.push(`searchTerm=${this.state.searchTerm}`);
    }

    Object.keys(filters).map(key => {
      if (filters[key].length) {
        hash.push(`${encodeURIComponent(key)}=${filters[key]}`);
      }
    });

    return hash.join('&');
  }

  hashToState(hash) {
    let state = {
      filters: {}
    };
    hash.split('&').map(param => {
      if (param.indexOf('=') > -1) {
        const [key, value] = param.split('=');
        if (key !== '') {
          if (key === 'page') {
            state.page = parseInt(value);
          } else if (key === 'searchTerm') {
            state.searchTerm = decodeURIComponent(value);
          } else if (key === 'sort') {
            state.sort = value;
          } else if (key === 'perPage') {
            state.perPage = value;
          } else {
            state.filters[key] = value.split(',');
          }
        }
      }
    });

    return state;
  }

  resetFilters() {
    return new Promise(resolve => {
      this.setState({
        filters: this.getDefaultFilters()
      }, resolve);
    });
  }

  handleBackButton() {
    if (this.isNonBackButtonEvent) {
      this.isNonBackButtonEvent = false;
      return;
    }

    this.processHash();
  }

  componentDidMount() {
    this.fetchFilters();

    window.addEventListener('hashchange', this.handleBackButton, false);
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    // unbind back button listener
    window.removeEventListener('hashchange', this.handleBackButton, false);
  }

  fetchProducts() {
    this.updateHash();

    // clear quantities
    this.setState({ quantities: {} });

    const params = Object.assign(
      {
        catId: this.props.catId,
        subCatId: this.props.subCatId,
        brandId: this.props.brandId,
        filter: this.props.filter,
        offset: Math.ceil(this.state.page * this.state.perPage),
        perPage: this.state.perPage,
        searchTerm: this.state.searchTerm,
        sort: this.state.sort,
        isNonOemFirst: this.props.isNonOemFirst ? 1 : 0,
      },
      this.state.filters
    );

    this.props.actions.fetchProducts(params);
  }

  fetchFilters() {
    const params = {
      catId: this.props.catId,
      subCatId: this.props.subCatId,
      brandId: this.props.brandId,
      searchTerm: this.state.searchTerm,
      filter: this.props.filter
    };

    this.props.actions.fetchFilters(params)
      .then(this.fetchProducts);
  }

  updateHash() {
    document.location.hash = '/' + this.stateToHash();
  }

  handleFilterChange(isOn, filterId, filterValue) {
    // create new filters state object
    let filters = Object.assign({}, this.state.filters),
      filter = filters[filterId];

    const index = filter.indexOf(filterValue);
    if (isOn) {
      if (index === -1) {
        filter.push(filterValue);
      }
    } else {
      if (index > -1) {
        filter.splice(index, 1);
      }
    }

    // clear pageYields if Ink/Toner or MICR aren't selected
    if (!filters.categories.includes('1') && !filters.categories.includes('2')) {
      filters.pageYields = [];
    }

    // update filter state
    this.isNonBackButtonEvent = true;
    this.setState({ page:0, filters }, this.fetchProducts);
  }

  handlePageChange(page) {
    this.isNonBackButtonEvent = true;
    this.setState({ page }, this.fetchProducts);

    // scroll to top
    window.scrollTo(0, 0);
  }

  handleSearch(e) {
    this.isNonBackButtonEvent = true;
    e.preventDefault();

    const searchTerm = this.refs.searchInput.value.trim();
    this.setState({ page: 0, searchTerm }, () => {
      this.fetchFilters();
    });
  }

  resetSearch(e) {
    this.refs.searchInput.value = '';
    this.handleSearch(e);
  }

  handleAddPrinter(e) {
    const printer = this.props.printer;
    this.props.actions.addSavedPrinter(printer.id, printer.name, printer.brandId);
  }

  handleRemovePrinter(e) {
    const printer = this.props.printer;
    this.props.actions.removeSavedPrinter(printer.id);
  }

  handleSort(e) {
    this.isNonBackButtonEvent = true;

    const sort = e.target.value;
    this.setState({ sort, page: 0 }, () => this.fetchProducts());
  }

  handleAddToMyProducts(id) {
    this.props.actions.addToMyProducts(id);
  }

  handleRemoveFromMyProducts(id) {
    this.props.actions.removeFromMyProducts(id);
  }

  handlePerPageChange(e) {
    this.isNonBackButtonEvent = true;

    const perPage = e.target.value;
    this.setState({ page: 0, perPage }, () => this.fetchProducts());
  }

  handleReset(e) {
    this.resetFilters().then(this.fetchProducts);
  }

  handleHelpClick(e) {
    e.preventDefault();
    window.psOpenWindow();
  }

  handleEmailLinkClick(e) {
    e.preventDefault();

    window.isNavigating = false;
    window.location.href = 'mailto:info@sundatasupply.com';
  }

  handleQtyChange(product, qty) {
    return new Promise(resolve => {
      const quantities = Object.assign({}, this.state.quantities);
      if (qty) {
        quantities[product.id] = Object.assign(product, { qty: parseInt(qty) });
      } else {
        delete quantities[product.id];
      }

      this.setState({ quantities }, resolve);
    });
  }

  handleAddToCart() {
    let items = {};

    Object.keys(this.state.quantities).map(id => {
      const product = this.state.quantities[id];
      if (product.qty) {
        items[product.id] = product;
      }
    });

    attemptAddToCart(Object.values(items)).then(() => {
      // clear qty selections
      this.setState({ quantities: {} });
    });
  }

  componentDidUpdate() {
    if (this.props.isFetchingFilters) return;

    // clear filter values when those filters are not visible
    filterNames.map(filterName => {

      if (!Object.keys(this.props.filters).includes(filterName) && this.state.filters[filterName].length) {
        this.setState({
          filters: Object.assign({}, this.state.filters, { [filterName]: [] })
        });
      } else if (this.state.filters[filterName] && this.props.filters[filterName]) {
        let newFiltersState = Object.assign({}, this.state.filters),
          isModified = false;

        // check for non-existent filter values
        this.state.filters[filterName].map(filterVal => {
          if (!Object.keys(this.props.filters[filterName]).includes(filterVal)) {
            isModified = true;
            newFiltersState[filterName] = newFiltersState[filterName].filter(val => filterVal != val);
          }
        });

        // update state if the filters have been modified
        if (isModified) {
          this.setState({
            filters: newFiltersState
          });
        }
      }
    });
  }

  render() {

    const products = this.props.products,
      printer = this.props.printer,
      filters = this.props.filters,
      filterValues = this.state.filters;

    // determine filters to show
    let activeFilters = {};
    Object.keys(filters).map(filterId => {
      if (Object.keys(filters[filterId]).length) {
        // skip page yields if Ink/Toner or MICR aren't selected
        if (filterId === 'pageYields' && !filterValues.categories.includes('1') && !filterValues.categories.includes('2')) {
          return;
        }

        activeFilters[filterId] = filters[filterId];
      }
    });

    const totalFilters = Object.keys(activeFilters).length;

    return (
      <div className="search-results">
        {
          (!this.props.isFetching && this.props.searchTerms != '' && !products.items.length)
            ?
              <div>
                <div className="row">
                  <div className="column small-12 medium-8">
                    <h1>No products found {this.state.searchTerm ? `for “${this.state.searchTerm}”` : ''}</h1>
                  </div>
                </div>

                <div className="content-container__small">
                  <p>Oops! We probably have what you're looking for. <a href="#" onClick={this.handleHelpClick}>Click here</a> to immediately be connected to someone 
                    via chat that will quickly let you know or simply give us a call at 800.388.8218. We are available M-F, 7 am to 4 pm PST.</p>
                  <p>Our search box is pretty particular. It really can only recognize exact machine model numbers and exact product part numbers - without many variations.
                    So you might try making your search term a little more exact.</p>
                  <p>Also, you can try shopping by product type using the Supply Finder at the top. (Select the brand, then the series, then your machine model number.)</p>
                  <p>Or, <a onClick={this.handleEmailLinkClick}>click here</a> to leave us an email and we'll respond to you by the next business day.</p>
                </div>
              </div>
            : (
              <div>
                <div className="search-results__header">
                  {
                    printer
                      ?
                      <div className="row">
                        <div className="column small-3 medium-2">
                          {
                            printer.image &&
                            <img src={`${ROOT_URL}/uploads/cat/${printer.image}`}/>
                          }
                        </div>
                        <div className="column small-9 medium-5">
                          {
                            printer.name &&
                            <div>
                              <h1>{this.props.titleOverride || printer.name}</h1>
                              {
                                printer.isSaved > -1 && (
                                  printer.isSaved
                                    ? <a onClick={this.handleRemovePrinter} className="button -gray small">Remove from Your Printers List</a>
                                    : <a onClick={this.handleAddPrinter} className="button -gray small">Add to Your Printers List</a>
                                )
                              }
                            </div>
                          }
                        </div>
                        <div className="column small-12 medium-5">
                          <form onSubmit={this.handleSearch} className="input-group">
                            <input type="text" className="input-group-field" placeholder={`Search only supplies for ${this.props.titleOverride || printer.name}`}
                                   ref="searchInput" defaultValue={this.state.searchTerm}/>
                            <div className="input-group-button">
                              <button className="button -gray small">Search Products</button>
                            </div>
                          </form>

                          {
                            this.state.searchTerm !== '' &&
                            <p><a className="link__clear-search" onClick={this.resetSearch}>Clear search results</a></p>
                          }

                          <div className="input-group search-results__header__per-page">
                            <span className="input-group-label">Display</span>
                            <select className="input-group-field" onChange={this.handlePerPageChange} value={this.state.perPage}>
                              <option value="10">10 per page</option>
                              <option value="20">20 per page</option>
                              <option value="50">50 per page</option>
                              <option value="1000">All</option>
                            </select>
                          </div>

                          <button className="button primary small products__button-add-all -top"
                                  disabled={!Object.keys(this.state.quantities).length}
                                  onClick={this.handleAddToCart}>Add all Selected to Cart</button>


                        </div>
                      </div>
                      :
                      <div className="row">
                        <div className="column small-12 medium-5">
                          {
                            !this.props.isFetching &&
                            <h1>{formatNumber(this.props.products.total)} {this.props.products.total === 1 ? 'result' : 'results'} for “{this.state.searchTerm}”</h1>
                          }
                        </div>
                        <div className="columns small-12 medium-7 text-right">
                          <div className="input-group-inline">
                            <div className="input-group">
                              <label className="input-group-label">Sort:</label>
                              <select className="input-group-field" onChange={this.handleSort} value={this.state.sort}>
                                <option value="relevance">Relevance</option>
                                <option value="price_asc">Price: Low to High</option>
                                <option value="price_desc">Price: High to Low</option>
                                <option value="name_asc">Name</option>
                              </select>
                            </div>
                          </div>

                          <div className="input-group-inline">
                            <div className="input-group search-results__header__per-page">
                              <span className="input-group-label">Display</span>
                              <select className="input-group-field" onChange={this.handlePerPageChange} value={this.state.perPage}>
                                <option value="10">10 per page</option>
                                <option value="20">20 per page</option>
                                <option value="50">50 per page</option>
                              </select>
                            </div>
                          </div>

                          <div className="clear-right"/>
                          <button className="button primary small products__button-add-all -no-margins"
                                  disabled={!Object.keys(this.state.quantities).length}
                                  onClick={this.handleAddToCart}>Add all Selected to Cart</button>
                        </div>
                      </div>
                  }
                </div>
                <div className="search-results__body">
                  <div className="row">
                    {
                      !!totalFilters &&
                      <div className="column small-12 medium-3">
                        {
                          <SearchResultsFilter filters={activeFilters} filterValues={filterValues} onChange={this.handleFilterChange}/>
                        }
                      </div>
                    }
                    <div className={`column small-12 medium-${totalFilters ? 9 : 12} -no-padding`}>
                      {
                        this.props.isFetching
                          ? <Loading/>
                          : <ProductsList products={products}
                                          onPageChange={this.handlePageChange}
                                          page={this.state.page}
                                          totalPages={Math.ceil(this.props.products.total / this.state.perPage)}
                                          onAddToMyProducts={this.handleAddToMyProducts}
                                          onRemoveFromMyProducts={this.handleRemoveFromMyProducts}
                                          onReset={this.handleReset}
                                          quantities={this.state.quantities}
                                          onQtyChange={this.handleQtyChange}
                                          onAddToCart={this.handleAddToCart}/>
                      }
                    </div>
                  </div>
                </div>
              </div>
            )
        }
      </div>
    );
  }
}

AppSearch.propTypes = {
  searchTerms: PropTypes.string,
  catId: PropTypes.number,
  subCatId: PropTypes.number,
  brandId: PropTypes.number,
  actions: PropTypes.object.isRequired,
  products: PropTypes.object.isRequired,
  filters: PropTypes.object.isRequired,
  printer: PropTypes.object,
  isFetching: PropTypes.bool.isRequired,
  isFetchingFilters: PropTypes.bool.isRequired,
  titleOverride: PropTypes.string,
  isNonOemFirst: PropTypes.bool.isRequired,
  isSearch: PropTypes.bool.isRequired,
};

function mapStateToProps(state) {
  return {
    isFetching: state.products.isFetching,
    isFetchingFilters: state.products.isFetchingFilters,
    products: state.products.products,
    printer: state.products.printer,
    filters: state.products.filters,
  };
}

function mapDispatchToProps(dispatch) {
  return {
  actions: bindActionCreators(actions, dispatch)
};
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(AppSearch);
